<section id="features" class="bg-white py-24">
  <div class="mx-auto flex max-w-page flex-col gap-16 px-4 sm:px-6 lg:px-8">
    <div class="mx-auto flex max-w-3xl flex-col gap-6 text-center">
      <div class="flex flex-col gap-3">
        <p class="text-caption font-semibold uppercase tracking-[0.2em] text-slate-500">Platform capabilities</p>
        <h2 class="text-h2 font-medium text-slate-950">Everything your forms need.</h2>
      </div>
      <p class="text-body1 leading-8 text-slate-600">
        Create forms, collect submissions, understand data, and automate simple operations from one workspace.
      </p>
    </div>

    <div class="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      <div class="rounded-[2rem] bg-slate-50 p-2">
        <div class="flex h-full flex-col gap-8 rounded-[1.5rem] bg-white p-7 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                  d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
              </svg>
            </div>
            <span class="text-caption text-slate-400">01</span>
          </div>
          <div class="flex flex-col gap-4">
            <h3 class="text-h6 text-slate-950">Form creation</h3>
            <p class="text-body2 leading-7 text-slate-600">
              Build clean forms for feedback, requests, onboarding, surveys, and internal workflows.
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-[2rem] bg-slate-50 p-2">
        <div class="flex h-full flex-col gap-8 rounded-[1.5rem] bg-white p-7 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-success-50 text-success-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M4 6h16M4 12h16M4 18h10" />
              </svg>
            </div>
            <span class="text-caption text-slate-400">02</span>
          </div>
          <div class="flex flex-col gap-4">
            <h3 class="text-h6 text-slate-950">Submissions</h3>
            <p class="text-body2 leading-7 text-slate-600">
              Store, view, filter, and track responses in a structured workspace-ready system.
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-[2rem] bg-slate-50 p-2">
        <div class="flex h-full flex-col gap-8 rounded-[1.5rem] bg-white p-7 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-50 text-secondary-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                  d="M4 19V5m4 14v-6m4 6V9m4 10v-9m4 9V7" />
              </svg>
            </div>
            <span class="text-caption text-slate-400">03</span>
          </div>
          <div class="flex flex-col gap-4">
            <h3 class="text-h6 text-slate-950">AI insights</h3>
            <p class="text-body2 leading-7 text-slate-600">
              Summarize responses, detect trends, and ask natural-language questions about your data.
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-[2rem] bg-slate-50 p-2">
        <div class="flex h-full flex-col gap-8 rounded-[1.5rem] bg-white p-7 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-warning-50 text-warning-700">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                  d="M9 12l2 2 4-4M7 4h10a2 2 0 012 2v4c0 5-3 8-7 10-4-2-7-5-7-10V6a2 2 0 012-2z" />
              </svg>
            </div>
            <span class="text-caption text-slate-400">04</span>
          </div>
          <div class="flex flex-col gap-4">
            <h3 class="text-h6 text-slate-950">Approvals</h3>
            <p class="text-body2 leading-7 text-slate-600">
              Review important submissions with simple approval states and assigned team members.
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-[2rem] bg-slate-50 p-2">
        <div class="flex h-full flex-col gap-8 rounded-[1.5rem] bg-white p-7 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-info-50 text-info-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                  d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m8-4a4 4 0 100-8 4 4 0 000 8zM9 10a4 4 0 100-8 4 4 0 000 8z" />
              </svg>
            </div>
            <span class="text-caption text-slate-400">05</span>
          </div>
          <div class="flex flex-col gap-4">
            <h3 class="text-h6 text-slate-950">Workspace teams</h3>
            <p class="text-body2 leading-7 text-slate-600">
              Invite members, assign roles, and keep form operations organized across your team.
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-[2rem] bg-slate-50 p-2">
        <div class="flex h-full flex-col gap-8 rounded-[1.5rem] bg-white p-7 shadow-sm">
          <div class="flex items-center justify-between">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-error-50 text-error-600">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span class="text-caption text-slate-400">06</span>
          </div>
          <div class="flex flex-col gap-4">
            <h3 class="text-h6 text-slate-950">Workflows</h3>
            <p class="text-body2 leading-7 text-slate-600">
              Trigger emails, webhooks, alerts, and connected actions when submissions are received.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
